"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { UI, DayFlag, SelectionState } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

const calendarClassNames: Partial<
  Record<
    | (typeof UI)[keyof typeof UI]
    | (typeof DayFlag)[keyof typeof DayFlag]
    | (typeof SelectionState)[keyof typeof SelectionState],
    string
  >
> = {
  [UI.Root]: "p-3",
  [UI.Months]: "flex flex-col sm:flex-row gap-4",
  [UI.Month]:
    "grid grid-cols-[auto_1fr_auto] gap-x-2 gap-y-4 items-center",
  [UI.MonthCaption]: "flex justify-center pt-1 items-center h-9",
  [UI.Nav]: "flex items-center justify-between w-full gap-2",
  [UI.PreviousMonthButton]:
    "size-9 shrink-0 rounded-md border bg-background inline-flex items-center justify-center opacity-100 hover:bg-accent hover:text-accent-foreground disabled:opacity-50 transition-colors",
  [UI.NextMonthButton]:
    "size-9 shrink-0 rounded-md border bg-background inline-flex items-center justify-center opacity-100 hover:bg-accent hover:text-accent-foreground disabled:opacity-50 transition-colors",
  [UI.CaptionLabel]: "text-sm font-medium",
  [UI.Weekdays]: "flex",
  [UI.Weekday]:
    "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] flex items-center justify-center",
  [UI.MonthGrid]: "col-span-3 w-full border-collapse mt-1",
  [UI.Weeks]: "flex flex-col gap-1",
  [UI.Week]: "flex w-full",
  [UI.Day]: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range_end)]:rounded-r-md",
  [UI.DayButton]: cn(
    buttonVariants({ variant: "ghost" }),
    "size-9 p-0 font-normal aria-selected:opacity-100 aspect-square rounded-md hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground transition-colors"
  ),
  [DayFlag.outside]:
    "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
  [DayFlag.today]: "bg-accent text-accent-foreground",
  [DayFlag.disabled]: "text-muted-foreground opacity-50",
  [SelectionState.selected]:
    "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
  [SelectionState.range_start]: "rounded-l-md",
  [SelectionState.range_end]: "rounded-r-md",
  [SelectionState.range_middle]:
    "rounded-none bg-accent text-accent-foreground",
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      navLayout="around"
      className={cn("rounded-lg border bg-background", className)}
      classNames={{ ...calendarClassNames, ...classNames }}
      components={{
        Chevron: ({ orientation, ...rest }) =>
          orientation === "left" ? (
            <ChevronLeft className="size-4" {...rest} />
          ) : (
            <ChevronRight className="size-4" {...rest} />
          ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
